import { Controller } from '@hotwired/stimulus';

const DEBOUNCE_MS = 300;
const PREVIEW_FIELDS = [
  'shop_branch_id',
  'purchased_at',
  'purchase_price',
  'bypass_multiplier_calculation',
];
const GUIDE_MESSAGE =
  '店舗・購入日時・合計金額・ランクによる倍率を入力すると表示されます';

// 購入履歴の追加で、入力内容から付与予定のポイント(スタンプ)数を取得して表示
export default class extends Controller {
  static targets = ['result', 'submit'];
  static values = { url: String };

  disconnect() {
    clearTimeout(this.timer);
    this.abortController?.abort();
  }

  update(event) {
    if (!PREVIEW_FIELDS.includes(event.target.name)) return;

    // 表示中の数が入力と食い違ったまま登録されないよう、取得し終えるまで登録させない
    this.setPending(true);
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.load(), DEBOUNCE_MS);
  }

  async load() {
    // 入力が続いたときに古いレスポンスで上書きしないよう、前のリクエストは中断する
    this.abortController?.abort();

    const formData = new FormData(this.element);
    if (PREVIEW_FIELDS.some((name) => !formData.get(name))) {
      this.renderMessage(GUIDE_MESSAGE);
      this.setPending(false);
      return;
    }

    this.abortController = new AbortController();

    const params = new URLSearchParams();
    for (const name of PREVIEW_FIELDS) {
      params.set(name, formData.get(name) ?? '');
    }

    try {
      const response = await fetch(`${this.urlValue}?${params}`, {
        headers: { Accept: 'application/json' },
        signal: this.abortController.signal,
      });
      const data = await response.json();
      if (response.ok) {
        this.renderPoints(data);
      } else {
        this.renderMessage(data.errors.join(' / '), { error: true });
      }
    } catch (error) {
      // 中断したのは後続のリクエストがあるときなので、待ち状態はそちらで解く
      if (error.name === 'AbortError') return;
      this.renderMessage('付与予定数を取得できませんでした', { error: true });
    }
    this.setPending(false);
  }

  setPending(pending) {
    this.submitTarget.disabled = pending;
    this.resultTarget.classList.toggle('is-pending', pending);
  }

  detail(data) {
    if (data.rank_name === null) return null;

    const rank = `現在のランク「${data.rank_name}」の倍率 ${data.point_multiplier}倍`;
    if (data.bypass_multiplier_calculation) return `${rank}は反映しません`;
    return `還元率による ${data.base_points.toLocaleString()}${data.unit} × ${rank}`;
  }

  renderPoints(data) {
    const amount = this.buildElement(
      'div',
      'p-orderPointPreview__amount',
      data.points.toLocaleString(),
    );
    amount.append(
      this.buildElement('span', 'p-orderPointPreview__unit', data.unit),
    );

    const detail = this.detail(data);
    this.resultTarget.replaceChildren(
      amount,
      ...(detail
        ? [this.buildElement('p', 'p-orderPointPreview__detail', detail)]
        : []),
    );
  }

  renderMessage(text, { error = false } = {}) {
    const className = error
      ? 'p-orderPointPreview__message p-orderPointPreview__message--error'
      : 'p-orderPointPreview__message';
    this.resultTarget.replaceChildren(this.buildElement('p', className, text));
  }

  // ランク名は事業者が設定した値のため、HTMLとして解釈させない
  buildElement(tagName, className, text) {
    const element = document.createElement(tagName);
    element.className = className;
    element.textContent = text;
    return element;
  }
}
